// Admin utilities for managing user roles with Firebase Custom Claims
import { auth } from '../firebase/config';

/**
 * Check if the current user has admin role
 * @returns {Promise<boolean>} True if user is admin
 */
export const isUserAdmin = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return false;

    // Get the user's ID token to access custom claims
    const idTokenResult = await user.getIdTokenResult();
    return idTokenResult.claims.admin === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Get user's custom claims (including roles)
 * @returns {Promise<Object>} User's custom claims
 */
export const getUserClaims = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return {};

    const idTokenResult = await user.getIdTokenResult();
    return idTokenResult.claims;
  } catch (error) {
    console.error('Error getting user claims:', error);
    return {};
  }
};

/**
 * Force refresh the user's ID token to get updated claims
 * Call this after admin status changes
 * @returns {Promise<void>}
 */
export const refreshUserToken = async () => {
  try {
    const user = auth.currentUser;
    if (user) {
      await user.getIdToken(true); // Force refresh
    }
  } catch (error) {
    console.error('Error refreshing user token:', error);
  }
};

/**
 * Check if user has a specific role
 * @param {string} role - Role to check for
 * @returns {Promise<boolean>} True if user has the role
 */
export const hasRole = async (role: string): Promise<boolean> => {
  try {
    const claims = await getUserClaims();
    return (claims as Record<string, unknown>)[role] === true;
  } catch (error) {
    console.error(`Error checking role ${role}:`, error);
    return false;
  }
};
